import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import {
  Grid,
  Typography,
  Box,
  useTheme,
  InputAdornment,
  Button,
  useMediaQuery,
} from "@mui/material";
import { Link, useHistory } from "react-router-dom";
import SecondaryCenterTemplate from "components/SecondaryCenterTemplate";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Field, reduxForm, getFormSyncErrors, getFormValues } from "redux-form";
import { renderTextField } from "components/FormFields/FieldTypes";
import {
  Required,
  PasswordMatch,
  OTPPassword,
  PasswordValidityMatch,
} from "components/FormFields/validators";
import { phone as phoneFormatter } from "components/FormFields/formatters";
import { homeActions } from "actions";
import ResentDialog from "components/ResentDialog";
import MessageDialog from "pages/SearchMedication/MessageDialog";

const ForgotVerifyPassword = (props) => {
  const theme = useTheme();
  const history = useHistory();
  const matchesMinWidth = useMediaQuery("(max-width:600px)");

  const {
    formErrors,
    formValues,
    loginForgotParams,
    loginVerifyForgotPassword,
    loginResendForgotPassword,
    forgotLoginPasswordInfo,
    resetLoginForgotPasswordResend,
    resendForgotLoginPasswordInfo,
    verifyForgotLoginPasswordInfo,
    resendForgotLoginPasswordStatus,
    resetLoginForgotPasswordStatus,
  } = props;
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showResendSuccess, setShowResendSuccess] = useState(false);
  const [resentMessage, setResentMessage] = useState("");

  const onFormSubmit = () => {
    loginVerifyForgotPassword({
      appUserLoginId: forgotLoginPasswordInfo.data,
      tempPassword: formValues.currentPassword,
      newPassword: formValues.newPassword,
    });
  };

  const resendAgain = () => {
    if (forgotLoginPasswordInfo && forgotLoginPasswordInfo.data) {
      loginResendForgotPassword(forgotLoginPasswordInfo.data);
    }
  };

  useEffect(() => {
    document.getElementById("appcontent").scroll(0, 0);
    return () => {
      resetLoginForgotPasswordStatus();
    };
  }, []);

  useEffect(() => {
    if (
      verifyForgotLoginPasswordInfo &&
      Object.keys(verifyForgotLoginPasswordInfo).length
    ) {
      setShowSuccess(true);
    }
  }, [verifyForgotLoginPasswordInfo]);

  useEffect(() => {
    if (resendForgotLoginPasswordStatus) {
      setShowResendSuccess(true);
      setResentMessage(resendForgotLoginPasswordInfo.message);
    }
  }, [resendForgotLoginPasswordStatus]);

  return (
    <SecondaryCenterTemplate pageTitle="Forgot Password">
      <Grid container>
        <Grid item sm={8} xs={12} sx={{ margin: "auto" }}>
          <form>
            <Box mb={1} mt={5} sx={{ textAlign: "center" }}>
              <Typography variant="h3LatoRegular">
                We sent a temporary password to:
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center" }} mb={4}>
              <Typography variant="text2">
                {loginForgotParams &&
                  (loginForgotParams.email ||
                    phoneFormatter(loginForgotParams.phone))}
              </Typography>
            </Box>
            <Grid item container alignItems="center" justifyContent="center">
              <Grid item xs={12} p={4} pt={0}>
                <Field
                  name="currentPassword"
                  isRequired
                  component={renderTextField}
                  label="Please enter the temporary password"
                  type={showCurrentPassword ? "text" : "password"}
                  validate={[Required, OTPPassword]}
                  handleKeyDown={e => {
                    if (e.key && e.key == 'Enter' && Object.keys(formErrors).length == 0) {
                      onFormSubmit();
                    }
                  }}
                  endIcon={
                    <InputAdornment
                      position="end"
                      onClick={() => {
                        setShowCurrentPassword(!showCurrentPassword);
                      }}
                    >
                      {showCurrentPassword ? (
                        <Visibility
                          sx={{ color: theme.palette.primary.main }}
                        />
                      ) : (
                        <VisibilityOff
                          sx={{ color: theme.palette.primary.main }}
                        />
                      )}
                    </InputAdornment>
                  }
                />
              </Grid>
              <Grid item xs={12} p={4} pt={0}>
                <Box sx={{ textAlign: "center" }}>
                  <Typography
                    variant="text3"
                    sx={{
                      color: theme.palette.common.greenThree,
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                    onClick={() => resendAgain()}
                  >
                    Resend temporary password
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} p={4} pt={0}>
                <Field
                  name="newPassword"
                  isRequired
                  component={renderTextField}
                  label="Please select a new memorable password"
                  type={showNewPassword ? "text" : "password"}
                  helperText="6 character minimum, 1 capital letter, 1 number"
                  validate={[Required, PasswordMatch, PasswordValidityMatch]}
                  handleKeyDown={e => {
                    if (e.key && e.key == 'Enter' && Object.keys(formErrors).length == 0) {
                      onFormSubmit();
                    }
                  }}
                  endIcon={
                    <InputAdornment
                      position="end"
                      onClick={() => {
                        setShowNewPassword(!showNewPassword);
                      }}
                    >
                      {showNewPassword ? (
                        <Visibility
                          sx={{ color: theme.palette.primary.main }}
                        />
                      ) : (
                        <VisibilityOff
                          sx={{ color: theme.palette.primary.main }}
                        />
                      )}
                    </InputAdornment>
                  }
                />
              </Grid>
              <Grid item xs={12} p={4} pt={0}>
                <Field
                  name="confirmPassword"
                  isRequired
                  component={renderTextField}
                  label="Please confirm your new password"
                  type={showConfirmPassword ? "text" : "password"}
                  validate={[Required, PasswordMatch, PasswordValidityMatch]}
                  handleKeyDown={e => {
                    if (e.key && e.key == 'Enter' && Object.keys(formErrors).length == 0) {
                      onFormSubmit();
                    }
                  }}
                  endIcon={
                    <InputAdornment
                      position="end"
                      onClick={() => {
                        setShowConfirmPassword(!showConfirmPassword);
                      }}
                    >
                      {showConfirmPassword ? (
                        <Visibility
                          sx={{ color: theme.palette.primary.main }}
                        />
                      ) : (
                        <VisibilityOff
                          sx={{ color: theme.palette.primary.main }}
                        />
                      )}
                    </InputAdornment>
                  }
                />
              </Grid>
              <Grid
                item
                xs={12}
                sm={12}
                sx={{ margin: "auto", marginTop: "54px", textAlign: "center" }}
              >
                <Box>
                  <Typography variant="h4LatoRegular">
                    By continuing to use this website, you agree to the Cuverd®
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    variant="h4LatoRegular"
                    sx={{ color: theme.palette.common.greenThree }}
                    component={Link}
                    to="/terms-and-conditions"
                  >
                    Terms and Conditions
                  </Typography>
                  <Typography variant="h4LatoRegular">
                    &nbsp;and&nbsp;
                  </Typography>
                  <Typography
                    variant="h4LatoRegular"
                    component={Link}
                    to="/privacy-policy"
                    sx={{ color: theme.palette.common.greenThree }}
                  >
                    Privacy Policy
                  </Typography>
                </Box>
              </Grid>
              <Grid
                item
                xs={12}
                sm={8}
                sx={{
                  margin: "auto",
                  marginTop: "34px",
                  marginBottom: "40px",
                  textAlign: "center",
                }}
              >
                <Box>
                  <Button
                    variant="large"
                    type="button"
                    disabled={Object.keys(formErrors).length ? true : false}
                    onClick={() => onFormSubmit()}
                    sx={{
                      minWidth: { xs: "100%", sm: "100%", md: undefined },
                      backgroundColor:
                        formErrors && Object.keys(formErrors).length
                          ? "#e6e6e6"
                          : undefined,
                      color:
                        formErrors && Object.keys(formErrors).length
                          ? "#637276"
                          : undefined,
                      boxShadow: "none",
                    }}
                  >
                    SAVE NEW PASSWORD
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
          {resendForgotLoginPasswordInfo ? (
            <ResentDialog
              showResentDialog={showResendSuccess}
              resentMessage={resentMessage}
              onResentClick={() => {
                resetLoginForgotPasswordResend();
                setShowResendSuccess(false);
              }}
            />
          ) : null}
          {showSuccess ? (
            <MessageDialog showPopUp={showSuccess}>
              <Box sx={{ maxWidth: "400px" }}>
                <Typography
                  variant={matchesMinWidth ? "h3LatoBold" : "h2LatoBold"}
                  sx={{ lineHeight: matchesMinWidth ? "24px" : "26px" }}
                >
                  {verifyForgotLoginPasswordInfo?.message}
                </Typography>
              </Box>
              <Box mt={5}>
                <Button
                  variant="large"
                  onClick={() => {
                    if (verifyForgotLoginPasswordInfo?.success) {
                      history.push("/");
                    }
                    setShowSuccess(false);
                  }}
                >
                  {verifyForgotLoginPasswordInfo?.success
                    ? "DONE"
                    : "TRY AGAIN"}
                </Button>
              </Box>
            </MessageDialog>
          ) : null}
        </Grid>
      </Grid>
    </SecondaryCenterTemplate>
  );
};

const mapStateToProps = (state) => {
  return {
    formErrors: getFormSyncErrors("FORGOT_VERIFY_PASSWORD_FORM")(state) || {},
    formValues: getFormValues("FORGOT_VERIFY_PASSWORD_FORM")(state),
    OTPValues: getFormValues("OTP_FIELD")(state),
    forgotLoginPasswordInfo: state.forgotLoginPassword.data,
    loginForgotParams: state.forgotLoginPassword.loginForgotParams,
    resendForgotLoginPasswordInfo: state.resendForgotLoginPassword.data,
    resendForgotLoginPasswordStatus: state.resendForgotLoginPassword.isSuccess,
    verifyForgotLoginPasswordInfo: state.verifyForgotLoginPassword.data,

    signUpInfo: state.signUp.response,
    signUpEmail: state.signUp.signUpEmail,
    verifyMessage:
      state.verifyPasswordChange && state.verifyPasswordChange.verifyMessage,
  };
};

const mapDispatchToProps = {
  verifyPasswordChange: (params) => homeActions.verifyPasswordChange(params),
  loginResendForgotPassword: (params) =>
    homeActions.loginResendForgotPassword(params),
  loginVerifyForgotPassword: (params) =>
    homeActions.loginVerifyForgotPassword(params),
  resetLoginForgotPasswordStatus: () =>
    homeActions.resetLoginForgotPasswordStatus(),
  resetLoginForgotPasswordResend: () =>
    homeActions.resetLoginForgotPasswordResend(),
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  reduxForm({
    form: "FORGOT_VERIFY_PASSWORD_FORM",
  })(ForgotVerifyPassword)
);
